import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArchiveRestore, Folder } from "lucide-react";
import { fetchArchivedProjects, archiveProject } from "../../services/projectService";
import "./Dashboard.css";

/**
 * ArchivedProjectsView - Modal showing all archived projects with unarchive option
 * @param {boolean} open - Whether modal is open
 * @param {Function} onClose - Close callback
 * @param {Function} onProjectRestored - Called when a project is unarchived so parent can refresh
 * @param {boolean} darkMode - Dark theme flag
 */
export default function ArchivedProjectsView({
  open,
  onClose,
  onProjectRestored,
  darkMode = false,
}) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    fetchArchivedProjects()
      .then((list) => {
        if (!cancelled) setProjects(list || []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleUnarchive = async (project) => {
    setRestoringId(project._id);
    try {
      await archiveProject(project._id, false);
      setProjects((prev) => prev.filter((p) => p._id !== project._id));
      onProjectRestored?.(project);
    } catch (err) {
      console.error("Error unarchiving project:", err);
    } finally {
      setRestoringId(null);
    }
  };

  if (!open || typeof document === "undefined") return null;

  const bg = darkMode ? "#0f172a" : "#f8fafc";
  const surface = darkMode ? "#1e293b" : "#ffffff";
  const border = darkMode ? "#334155" : "#e2e8f0";
  const text = darkMode ? "#e2e8f0" : "#1e293b";
  const muted = darkMode ? "#64748b" : "#94a3b8";

  const dialogContent = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000000,
        pointerEvents: "none",
      }}
    >
      <motion.div
        role="dialog"
        aria-label="Archived Projects"
        aria-modal="true"
        initial={{ opacity: 0, scale: 0.95, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 8 }}
        transition={{ duration: 0.18 }}
        style={{
          width: "480px",
          maxWidth: "94vw",
          maxHeight: "70vh",
          pointerEvents: "auto",
          backgroundColor: surface,
          border: `1px solid ${border}`,
          borderRadius: "0.875rem",
          boxShadow: darkMode
            ? "0 20px 50px rgba(0,0,0,0.6)"
            : "0 20px 50px rgba(0,0,0,0.14)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1.25rem 1.5rem 1rem",
            borderBottom: `1px solid ${border}`,
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "0.5rem",
                background: darkMode ? "#1e3a5f" : "#dbeafe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Folder size={16} color={darkMode ? "#60a5fa" : "#2563eb"} />
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: "1.0625rem",
                fontWeight: 700,
                color: text,
              }}
            >
              Archived Projects
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "0.375rem",
              color: muted,
              display: "flex",
              alignItems: "center",
              borderRadius: "0.375rem",
            }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: "1 1 0%",
            overflowY: "auto",
            padding: "0.75rem 1.25rem 1.25rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
          }}
        >
          {loading ? (
            <div
              style={{
                padding: "2rem",
                textAlign: "center",
                color: muted,
                fontSize: "0.9375rem",
              }}
            >
              Loading archived projects…
            </div>
          ) : projects.length === 0 ? (
            <div
              style={{
                padding: "2.5rem 1rem",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.75rem",
                color: muted,
              }}
            >
              <Folder size={36} style={{ opacity: 0.4 }} />
              <p style={{ margin: 0, fontSize: "0.9375rem" }}>
                No archived projects
              </p>
              <p style={{ margin: 0, fontSize: "0.8125rem", opacity: 0.7 }}>
                Archive a project to hide it from the sidebar without deleting it.
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
              {/* Research Projects */}
              {projects.filter((p) => !p.mode || p.mode === "dashboard").length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "0.5rem", 
                    padding: "0 0.5rem",
                    marginBottom: "0.25rem"
                  }}>
                    <div style={{ 
                      height: "1px", 
                      flex: 1, 
                      backgroundColor: border,
                      opacity: 0.6
                    }} />
                    <span style={{ 
                      fontSize: "0.75rem", 
                      fontWeight: 700, 
                      color: muted, 
                      textTransform: "uppercase",
                      letterSpacing: "0.05em"
                    }}>Research</span>
                    <div style={{ 
                      height: "1px", 
                      flex: 1, 
                      backgroundColor: border,
                      opacity: 0.6
                    }} />
                  </div>
                  <AnimatePresence mode="popLayout">
                    {projects
                      .filter((p) => !p.mode || p.mode === "dashboard")
                      .map((p) => (
                        <motion.div
                          key={p._id}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.18 }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "0.75rem 1rem",
                            background: darkMode ? "#0f172a" : "#f8fafc",
                            border: `1px solid ${border}`,
                            borderRadius: "0.625rem",
                            gap: "0.75rem",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", minWidth: 0 }}>
                            <Folder size={15} color={muted} style={{ flexShrink: 0 }} />
                            <div style={{ minWidth: 0 }}>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "0.9375rem",
                                  fontWeight: 600,
                                  color: text,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {p.name}
                              </p>
                              {p.archivedAt && (
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: "0.75rem",
                                    color: muted,
                                  }}
                                >
                                  Archived{" "}
                                  {new Date(p.archivedAt).toLocaleDateString(undefined, {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={restoringId === p._id}
                            onClick={() => handleUnarchive(p)}
                            style={{
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: "0.375rem",
                              padding: "0.4rem 0.75rem",
                              fontSize: "0.8125rem",
                              border: `1px solid ${darkMode ? "#475569" : "#cbd5e1"}`,
                              borderRadius: "0.5rem",
                              background: "transparent",
                              color: darkMode ? "#93c5fd" : "#2563eb",
                              cursor: restoringId === p._id ? "not-allowed" : "pointer",
                              fontWeight: 600,
                              opacity: restoringId === p._id ? 0.65 : 1,
                              transition: "opacity 0.15s",
                            }}
                          >
                            <ArchiveRestore size={13} />
                            {restoringId === p._id ? "Restoring…" : "Unarchive"}
                          </button>
                        </motion.div>
                      ))}
                  </AnimatePresence>
                </div>
              )}

              {/* Debate Projects */}
              {projects.filter((p) => p.mode === "debate").length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "0.5rem", 
                    padding: "0 0.5rem",
                    marginBottom: "0.25rem"
                  }}>
                    <div style={{ 
                      height: "1px", 
                      flex: 1, 
                      backgroundColor: border,
                      opacity: 0.6
                    }} />
                    <span style={{ 
                      fontSize: "0.75rem", 
                      fontWeight: 700, 
                      color: muted, 
                      textTransform: "uppercase",
                      letterSpacing: "0.05em"
                    }}>Debate</span>
                    <div style={{ 
                      height: "1px", 
                      flex: 1, 
                      backgroundColor: border,
                      opacity: 0.6
                    }} />
                  </div>
                  <AnimatePresence mode="popLayout">
                    {projects
                      .filter((p) => p.mode === "debate")
                      .map((p) => (
                        <motion.div
                          key={p._id}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.18 }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "0.75rem 1rem",
                            background: darkMode ? "#0f172a" : "#f8fafc",
                            border: `1px solid ${border}`,
                            borderRadius: "0.625rem",
                            gap: "0.75rem",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", minWidth: 0 }}>
                            <Folder size={15} color={muted} style={{ flexShrink: 0 }} />
                            <div style={{ minWidth: 0 }}>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "0.9375rem",
                                  fontWeight: 600,
                                  color: text,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {p.name}
                              </p>
                              {p.archivedAt && (
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: "0.75rem",
                                    color: muted,
                                  }}
                                >
                                  Archived{" "}
                                  {new Date(p.archivedAt).toLocaleDateString(undefined, {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={restoringId === p._id}
                            onClick={() => handleUnarchive(p)}
                            style={{
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              gap: "0.375rem",
                              padding: "0.4rem 0.75rem",
                              fontSize: "0.8125rem",
                              border: `1px solid ${darkMode ? "#475569" : "#cbd5e1"}`,
                              borderRadius: "0.5rem",
                              background: "transparent",
                              color: darkMode ? "#93c5fd" : "#2563eb",
                              cursor: restoringId === p._id ? "not-allowed" : "pointer",
                              fontWeight: 600,
                              opacity: restoringId === p._id ? 0.65 : 1,
                              transition: "opacity 0.15s",
                            }}
                          >
                            <ArchiveRestore size={13} />
                            {restoringId === p._id ? "Restoring…" : "Unarchive"}
                          </button>
                        </motion.div>
                      ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );

  return createPortal(
    <>
      <div
        role="presentation"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999999,
          backgroundColor: "rgba(0,0,0,0.3)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
        onClick={onClose}
      />
      {dialogContent}
    </>,
    document.body
  );
}
